import {
  AppCard,
  Button,
  FormAsyncAutocomplete,
  Icon,
  InfoField,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { mdiAccount, mdiContentSaveOutline } from "@mdi/js";
import { Box } from "@mui/material";
import {
  queryOptions,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type {
  AssignmentRule,
  AssignmentRuleUpdate,
} from "../../../../@types/services/AssignmentRule";
import type { MmRequestReason } from "../../../../@types/services/MmRequestReason";
import type { PlantField } from "../../../../@types/services/PlantField";
import type { RequesterArea } from "../../../../@types/services/RequesterArea";
import { listPlants } from "../../../../__generated__/orval/endpoints/plants/plants";
import type { ListPlantsParams } from "../../../../__generated__/orval/models/listPlantsParams.zod";
import { ForbiddenError } from "../../../../errors/ForbiddenError";
import {
  fetchAssignmentRule,
  updateAssignmentRule,
} from "../../../../services/assignmentRules";
import { fetchMmRequestReasons } from "../../../../services/mmRequestReasons";
import { fetchPlantFields } from "../../../../services/plantFields";
import { fetchRequesterAreas } from "../../../../services/requesterAreas";
import {
  type EditMmRequestAssignmentRuleFormValues,
  editMmRequestAssignmentRuleFormSchema,
} from "../-schemas/editMmRequestAssignmentRuleFormSchema";

const loaderQueryOptions = (assignmentRuleId: string) =>
  queryOptions({
    queryFn: () => fetchAssignmentRule(assignmentRuleId),
    queryKey: ["mmRequestAssignmentRules", "detail", assignmentRuleId],
    staleTime: 1000 * 60 * 5,
  });

const EditMmRequestAssignmentRule: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { assignmentRuleId } = Route.useParams();
  const { data: assignmentRule } = useSuspenseQuery(
    loaderQueryOptions(assignmentRuleId),
  );

  const { handleSubmit, control } = useForm({
    defaultValues: {
      mm_request_reasons:
        assignmentRule?.mm_request_reasons.map(
          (reason) => reason.mm_request_reason,
        ) || [],
      plant_fields:
        assignmentRule?.plant_fields.map((field) => field.plant_field) || [],
      plants: assignmentRule?.plants.map((plant) => plant.plant) || [],
      requester_areas:
        assignmentRule?.requester_areas.map((area) => area.requester_area) ||
        [],
    },
    mode: "onBlur",
    resolver: zodResolver(editMmRequestAssignmentRuleFormSchema),
  });

  const { mutate: update, isPending: isUpdating } = useMutation<
    AssignmentRule,
    Error,
    AssignmentRuleUpdate
  >({
    mutationFn: (data) => updateAssignmentRule(data),
    onError: () => {
      enqueueSnackbar(t("assignmentRules.messages.updateError"), {
        variant: "error",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["mmRequestAssignmentRules"],
      });
      enqueueSnackbar(t("assignmentRules.messages.updateSuccess"), {
        variant: "success",
      });
      navigate({ to: "/mm_request_assignment_rules" });
    },
  });

  const onSubmit: SubmitHandler<EditMmRequestAssignmentRuleFormValues> =
    useCallback(
      (data) => {
        const formattedData: AssignmentRuleUpdate = {
          id: assignmentRule.id,

          mm_request_reasons_attributes: [
            ...assignmentRule.mm_request_reasons
              .filter(
                (existingItem) =>
                  !data.mm_request_reasons.some(
                    (newItem) =>
                      newItem.id === existingItem.mm_request_reason.id,
                  ),
              )
              .map((item) => ({ _destroy: true, id: item.id })),
            ...data.mm_request_reasons
              .filter(
                (newItem) =>
                  !assignmentRule.mm_request_reasons.some(
                    (existingItem) =>
                      existingItem.mm_request_reason.id === newItem.id,
                  ),
              )
              .map((item) => ({ mm_request_reason_id: item.id })),
          ],

          plant_fields_attributes: [
            ...assignmentRule.plant_fields
              .filter(
                (existingItem) =>
                  !data.plant_fields.some(
                    (newItem) => newItem.id === existingItem.plant_field.id,
                  ),
              )
              .map((item) => ({
                _destroy: true,
                id: item.id,
              })),
            ...data.plant_fields
              .filter(
                (newItem) =>
                  !assignmentRule.plant_fields.some(
                    (existingItem) =>
                      existingItem.plant_field.id === newItem.id,
                  ),
              )
              .map((item) => ({
                plant_field_id: item.id,
              })),
          ],

          plants_attributes: [
            ...assignmentRule.plants
              .filter(
                (existingItem) =>
                  !data.plants.some(
                    (newItem) => newItem.id === existingItem.plant.id,
                  ),
              )
              .map((item) => ({
                _destroy: true,
                id: item.id,
              })),
            ...data.plants
              .filter(
                (newItem) =>
                  !assignmentRule.plants.some(
                    (existingItem) => existingItem.plant.id === newItem.id,
                  ),
              )
              .map((item) => ({ plant_id: item.id })),
          ],

          requester_areas_attributes: [
            ...assignmentRule.requester_areas
              .filter(
                (existingItem) =>
                  !data.requester_areas.some(
                    (newItem) => newItem.id === existingItem.requester_area.id,
                  ),
              )
              .map((item) => ({
                _destroy: true,
                id: item.id,
              })),
            ...data.requester_areas
              .filter(
                (newItem) =>
                  !assignmentRule.requester_areas.some(
                    (existingItem) =>
                      existingItem.requester_area.id === newItem.id,
                  ),
              )
              .map((item) => ({
                requester_area_id: item.id,
              })),
          ],
        };

        update(formattedData);
      },
      [assignmentRule, update],
    );

  return (
    <PageContainer
      maxWidth="md"
      showBackButton
      title={t("assignmentRules.titles.edit")}
    >
      <AppCard>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              width: "100%",
            }}
          >
            <InfoField
              label={t("assignmentRules.labels.user")}
              labelIconPath={mdiAccount}
              text={assignmentRule.user?.full_name_with_login || ""}
            />

            <FormAsyncAutocomplete
              control={control}
              fetchOptions={fetchMmRequestReasons}
              filters={(searchTerm: string) => ({
                enabled: true,
                title_like: searchTerm,
              })}
              getOptionLabel={(option: MmRequestReason) => option.title || ""}
              label={t("assignmentRules.labels.mmRequestReasons")}
              multiple
              name="mm_request_reasons"
              queryKey={["mmRequestReasons", "infinite"]}
              required
            />

            <FormAsyncAutocomplete
              control={control}
              fetchOptions={fetchPlantFields}
              filters={(searchTerm: string) => ({
                active_on_mm_request: true,
                name: searchTerm,
              })}
              getOptionLabel={(option: PlantField) => option.name || ""}
              label={t("assignmentRules.labels.plantFields")}
              multiple
              name="plant_fields"
              queryKey={["plantFields", "infinite"]}
              required
            />

            <FormAsyncAutocomplete
              control={control}
              fetchOptions={fetchRequesterAreas}
              filters={(searchTerm: string) => ({
                enabled: true,
                name: searchTerm,
              })}
              getOptionLabel={(option: RequesterArea) => option.name || ""}
              label={t("assignmentRules.labels.requesterAreas")}
              multiple
              name="requester_areas"
              queryKey={["requesterAreas", "infinite"]}
              required
            />

            <FormAsyncAutocomplete
              control={control}
              fetchOptions={listPlants}
              filters={(searchTerm): ListPlantsParams => ({
                active_on_mm_request: true,
                plant_number_with_name: searchTerm,
              })}
              getOptionLabel={(option) => option.plant_number_with_name}
              helperText={t("assignmentRules.helpers.plants")}
              label={t("assignmentRules.labels.plants")}
              minSearchLength={2}
              multiple
              name="plants"
              queryKey={["plants", "infinite"]}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                color="success"
                loading={isUpdating}
                startIcon={<Icon icon={mdiContentSaveOutline} />}
                type="submit"
                variant="contained"
              >
                {t("common.save")}
              </Button>
            </Box>
          </Box>
        </form>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/mm_request_assignment_rules/$assignmentRuleId/edit",
)({
  beforeLoad({ context }) {
    if (context.auth.user?.roles_names.includes("Assignment Rules - Update")) {
      return;
    }

    throw new ForbiddenError();
  },
  component: EditMmRequestAssignmentRule,
  loader: ({ context, params: { assignmentRuleId } }) =>
    context.queryClient.ensureQueryData(loaderQueryOptions(assignmentRuleId)),
});
